import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  createEpicorJob,
  getCustomerCredit,
  getEpicorCustomer,
  listCustomerInvoices,
  refreshCustomerCredit,
  listCustomerOrders,
  listEpicorJobs,
  refreshBCCustomers,
  setManualCustomerMatch,
  subscribeToEpicorJob,
} from '../../services/epicorApi';
import { StatusBadge } from '../ui';
import CustomerTagsPanel from './CustomerTagsPanel';
import {
  fmtAmt as fmtOrderAmt,
  fmtDate as fmtOrderDate,
  orderedDisplay,
} from '../orders/orderFormat';
import './CustomerDetail.css';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : (n ?? '—'));
const fmtAmt = (n) =>
  n == null || n === ''
    ? '—'
    : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');
const fmtDate = (t) =>
  t ? new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';

const addrLine = (row) => [row.address1, row.address2, row.address3].filter(Boolean).join(', ');
const regionLine = (row) => [row.city, row.state, row.zip, row.country].filter(Boolean).join(', ');

const Corners = () => (
  <>
    <i className="oc-corner tl" aria-hidden="true" />
    <i className="oc-corner tr" aria-hidden="true" />
    <i className="oc-corner bl" aria-hidden="true" />
    <i className="oc-corner br" aria-hidden="true" />
  </>
);

const MetaChip = ({ label, value, tone }) => (
  <span className="oc-metachip">
    <i className={`oc-dot oc-dot-${tone}`} aria-hidden="true" />
    <span className="oc-metachip-key">{label}</span>
    <span className="oc-metachip-val">{value}</span>
  </span>
);

const Stat = ({ value, label, hint, text, strong, action }) => (
  <div className={`oc-stat oc-stat-static oc-blueprint${strong ? ' strong' : ''}`}>
    <Corners />
    <span className={`oc-stat-value${text ? ' text' : ''}`}>{value}</span>
    <span className="oc-stat-label">{label}</span>
    {hint && <span className="oc-stat-hint">{hint}</span>}
    {action}
  </div>
);

const QUARANTINE_COPY = {
  currency_mixed:
    'This customer has open documents in more than one currency. Summing them '
    + 'would produce a meaningless figure, so no credit position is published.',
  orders_missing_amt:
    'One or more open orders have no amount recorded. Totalling the rest would '
    + 'understate what the customer owes, in their favour.',
  customer_missing:
    'This customer is staged in the middleware but could not be found in '
    + 'Epicor. Their exposure cannot be read, so no credit is offered.',
};

const BreakdownRow = ({ label, value, note, excluded, strong }) => (
  <div className={`oc-cb-row${excluded ? ' excluded' : ''}${strong ? ' strong' : ''}`}>
    <span className="oc-cb-label">
      {label}
      {note && <span className="oc-cb-note">{note}</span>}
    </span>
    <span className="oc-cb-value">{fmtAmt(value)}</span>
  </div>
);

/* Reproduces Epicor's Customer Credit screen, which is what Waterco staff
   reconcile against. Epicor shows the CredInc flags as checkboxes for a reason:
   an excluded line still has a figure, and rendering it as though it counted
   would make the total look like an arithmetic error.

   PI credit is not shown. It is hardcoded to zero at Waterco -- no customer
   uses Epicor's purchase-installment feature -- so a permanent 0.00 row would
   be noise. Restore it here if that ever changes. */
const CreditBreakdown = ({ credit, provenance, reservedAmt }) => {
  const quarantined = Boolean(credit.quarantine_reason);
  const ordersCounted = credit.credit_include_orders !== false;

  return (
    <section className="oc-frame oc-blueprint oc-credit-panel" aria-label="Credit breakdown">
      <Corners />
      <div className="oc-cb-head">
        <h2 className="oc-cb-title">Credit</h2>
        {provenance && (
          <MetaChip label="Source" value={provenance.text} tone={provenance.tone} />
        )}
      </div>

      {quarantined ? (
        /* Every line would be a placeholder: the snapshot nulls the limit AND
           zeroes the exposure when it refuses to assess. Showing 0.00 six times
           would read as "this customer owes nothing". */
        <div className="oc-cb-blocked">
          <p className="oc-cb-blocked-title">Credit cannot be assessed</p>
          <p className="oc-cb-blocked-copy">
            {QUARANTINE_COPY[credit.quarantine_reason]
              || `Not assessable (${credit.quarantine_reason}).`}
          </p>
        </div>
      ) : (
        <div className="oc-cb-rows">
          <BreakdownRow label="Credit limit" value={credit.credit_limit} />
          <BreakdownRow label="Total invoiced" value={credit.invoice_credit} />
          <BreakdownRow
            label="Orders pending"
            value={credit.order_credit}
            excluded={!ordersCounted}
            note={ordersCounted ? null : 'not counted — CredInc Orders is off in Epicor'}
          />
          <BreakdownRow
            label="Held for web orders"
            value={credit.reserved_credit}
            note={
              reservedAmt > 0
                ? `${credit.reservation_count} order${credit.reservation_count === 1 ? '' : 's'} not yet in Epicor`
                : null
            }
          />
          <BreakdownRow label="Credit total" value={credit.credit_total} strong />
          <BreakdownRow label="Available credit" value={credit.available_credit} strong />
        </div>
      )}
    </section>
  );
};

const TABS = [
  { id: 'shiptos', label: 'Ship-to addresses' },
  { id: 'orders', label: 'Sales orders' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'link', label: 'BigCommerce link' },
  { id: 'activity', label: 'Activity' },
];
const ORDER_PAGE = 25;
const INVOICE_PAGE = 25;
const DOC_TYPES = [
  { id: 'all', label: 'All' },
  { id: 'invoice', label: 'Invoices' },
  { id: 'credit_memo', label: 'Credit notes' },
];

/* ──────────────────────────────────────────────────────────────
   Customer detail (routed): header with meta chips, stat cards,
   and Ship-tos / BigCommerce link / Activity tabs. Storefront
   data stays read-only; only middleware mappings can be changed.
   ────────────────────────────────────────────────────────────── */
const CustomerDetailPage = () => {
  const { company, custNum } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : 'shiptos';
  const setTab = (t) => setSearchParams(t === 'shiptos' ? {} : { tab: t }, { replace: true });

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Targeted ship-to sync (per-customer refresh from Epicor).
  const [shipSyncing, setShipSyncing] = useState(false);
  const [shipLive, setShipLive] = useState(null);
  const [shipError, setShipError] = useState(null);
  const shipUnsubRef = useRef(null);

  const [shipQuery, setShipQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [jobs, setJobs] = useState(null);

  const [orderRows, setOrderRows] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const [orderPage, setOrderPage] = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);

  const [invoiceRows, setInvoiceRows] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [invoiceError, setInvoiceError] = useState(null);
  const [invoicePage, setInvoicePage] = useState(0);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [docType, setDocType] = useState('all');

  // Credit availability. Loads with the page rather than behind a tab: it gates
  // buy-on-account, so it belongs in the header stats next to Open AR.
  const [credit, setCredit] = useState(null);
  const [creditError, setCreditError] = useState(null);
  const [creditSyncing, setCreditSyncing] = useState(false);
  const creditUnsubRef = useRef(null);

  const loadCredit = useCallback(async () => {
    try {
      setCreditError(null);
      setCredit(await getCustomerCredit(company, custNum));
    } catch (e) {
      // 404 just means no snapshot has been computed yet, which is a normal
      // state and not worth an error banner.
      if (e.response?.status === 404) setCredit(undefined);
      else setCreditError(e.response?.data?.detail || e.message);
    }
  }, [company, custNum]);

  const load = useCallback(async () => {
    try {
      setError(null);
      const d = await getEpicorCustomer(company, custNum);
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [company, custNum]);

  useEffect(() => {
    setData(null);
    setShipQuery('');
    setJobs(null);
    setCredit(null);
    load();
    loadCredit();
  }, [load, loadCredit]);

  // Tear down any open sync streams on unmount.
  useEffect(() => () => {
    if (shipUnsubRef.current) { shipUnsubRef.current(); shipUnsubRef.current = null; }
    if (creditUnsubRef.current) { creditUnsubRef.current(); creditUnsubRef.current = null; }
  }, []);

  // Enqueues a live recompute and waits for the job to close before re-reading.
  // The refresh is async by design: it re-pulls the customer from Epicor over
  // the SSH tunnel, which is too slow to block a request on.
  const syncCredit = async () => {
    setCreditError(null);
    setCreditSyncing(true);
    try {
      const { job_id: jobId } = await refreshCustomerCredit(company, {
        custNum: Number(custNum),
        live: true,
      });
      if (creditUnsubRef.current) creditUnsubRef.current();
      creditUnsubRef.current = subscribeToEpicorJob(
        jobId,
        () => {},
        async () => {
          creditUnsubRef.current = null;
          setCreditSyncing(false);
          await loadCredit();
        },
      );
    } catch (e) {
      // 429 is the rate cap, not a failure: the snapshot on screen is current.
      setCreditError(e.response?.status === 429
        ? 'Refreshed moments ago — showing the current snapshot.'
        : (e.response?.data?.detail || e.message));
      setCreditSyncing(false);
    }
  };

  const syncShipTos = async () => {
    setShipError(null);
    setShipLive(null);
    setShipSyncing(true);
    try {
      const { job_id } = await createEpicorJob({
        companies: [company],
        stage: 'shipto',
        cust_nums: [Number(custNum)],
      });
      if (shipUnsubRef.current) shipUnsubRef.current();
      shipUnsubRef.current = subscribeToEpicorJob(
        job_id,
        (payload) => setShipLive(payload),
        async () => {
          shipUnsubRef.current = null;
          setShipSyncing(false);
          await load();
        },
      );
    } catch (e) {
      setShipError(e.response?.data?.detail || e.message);
      setShipSyncing(false);
    }
  };

  const link = async (bcId) => {
    setSaving(true);
    try {
      await setManualCustomerMatch(company, custNum, bcId);
      await load();
    } catch (e) {
      alert(`Match update failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const refreshBC = async () => {
    setRefreshing(true);
    try {
      await refreshBCCustomers([company]);
      await load();
    } catch (e) {
      alert(`BC refresh failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Activity tab: lazy-load recent jobs targeted at this customer.
  useEffect(() => {
    if (tab !== 'activity' || jobs !== null) return undefined;
    let alive = true;
    (async () => {
      try {
        const all = await listEpicorJobs({ limit: 25 });
        if (!alive) return;
        const n = Number(custNum);
        setJobs((all || []).filter((j) => {
          const targeted =
            (j.payload && j.payload.cust_nums) ||
            (j.counters && j.counters.targeted_cust_ids) ||
            [];
          return targeted.map(Number).includes(n);
        }));
      } catch (e) {
        if (alive) setJobs([]);
      }
    })();
    return () => { alive = false; };
  }, [tab, jobs, custNum]);

  // Orders tab: paginated archival orders for this customer.
  useEffect(() => {
    if (tab !== 'orders') return undefined;
    let alive = true;
    (async () => {
      setOrderError(null);
      try {
        const data = await listCustomerOrders(company, custNum, {
          limit: ORDER_PAGE,
          offset: orderPage * ORDER_PAGE,
        });
        if (!alive) return;
        setOrderRows(data.orders || []);
        setOrderTotal(data.total || 0);
        setOrderStats(data.stats || {});
      } catch (e) {
        if (alive) {
          setOrderError(e.response?.data?.detail || e.message);
          setOrderRows([]);
        }
      }
    })();
    return () => { alive = false; };
  }, [tab, company, custNum, orderPage]);

  // Invoices tab: invoices and credit notes, filtered by document type.
  // The stats block comes back unfiltered, so the badge and Open AR card hold
  // still while the filter moves.
  useEffect(() => {
    if (tab !== 'invoices') return undefined;
    let alive = true;
    (async () => {
      setInvoiceError(null);
      try {
        const res = await listCustomerInvoices(company, custNum, {
          doc_type: docType,
          limit: INVOICE_PAGE,
          offset: invoicePage * INVOICE_PAGE,
        });
        if (!alive) return;
        setInvoiceRows(res.invoices || []);
        setInvoiceTotal(res.total || 0);
        setInvoiceStats(res.stats || {});
      } catch (e) {
        if (alive) {
          setInvoiceError(e.response?.data?.detail || e.message);
          setInvoiceRows([]);
        }
      }
    })();
    return () => { alive = false; };
  }, [tab, company, custNum, invoicePage, docType]);

  const changeDocType = (id) => {
    setDocType(id);
    setInvoicePage(0);
    setInvoiceRows(null);
  };

  const backLink = (
    <Link to="/customers" className="oc-back">← All customers</Link>
  );

  if (error) {
    return (
      <div className="oc-scope oc-detail-page">
        <div className="oc-header oc-detail-header">
          <div className="oc-header-text">{backLink}</div>
        </div>
        <div className="oc-frame oc-blueprint">
          <Corners />
          <div className="oc-error" role="alert">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="oc-scope oc-detail-page">
        <div className="oc-header oc-detail-header">
          <div className="oc-header-text">
            {backLink}
            <div className="ui-skel" style={{ height: 36, width: 200, marginTop: 10 }} />
          </div>
        </div>
        <div className="oc-stats">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="oc-stat oc-stat-static oc-blueprint">
              <Corners />
              <div className="ui-skel" style={{ height: 34, width: '55%' }} />
              <div className="ui-skel" style={{ height: 13, width: '75%', marginTop: 8 }} />
            </div>
          ))}
        </div>
        <div className="oc-frame oc-blueprint">
          <Corners />
          <div style={{ padding: 'var(--oc-space-6)' }}>
            <div className="ui-skel" style={{ height: 16, width: '40%', marginBottom: 14 }} />
            {[0, 1, 2].map((i) => (
              <div key={i} className="ui-skel" style={{ height: 14, width: `${82 - i * 14}%`, marginBottom: 10 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const c = data.customer;
  const shipTos = data.ship_tos || [];
  const mapping = data.bc_mapping || null;
  const linked = Boolean(mapping && mapping.bc_customer_id != null);
  const bc = (mapping && mapping.bc_customer) || null;
  const bcAttrs = (bc && bc.attributes) || [];
  const bcAddrCount = bc ? (bc.addresses || []).length : 0;
  const candidates = data.bc_candidates || [];
  const cities = new Set(shipTos.map((s) => (s.city || '').trim()).filter(Boolean));

  const linkStat = linked
    ? {
        value: `Linked to BC #${mapping.bc_customer_id}`,
        hint: `Matched on ${fmtDate(mapping.matched_at)}`,
        text: true,
      }
    : c.match_status === 'ambiguous'
      ? {
          value: 'Needs review',
          hint: `${fmt(candidates.length)} candidate${candidates.length === 1 ? '' : 's'} in the snapshot`,
          text: true,
          strong: true,
          action: (
            <button
              type="button"
              className="oc-btn oc-btn-secondary oc-stat-action"
              onClick={() => setTab('link')}
            >
              Resolve
            </button>
          ),
        }
      : { value: 'Not linked', hint: 'No storefront record found', text: true };

  const qlc = shipQuery.trim().toLowerCase();
  const shownShipTos = qlc
    ? shipTos.filter((s) =>
        [s.ship_to_num, s.name, s.address1, s.address2, s.address3, s.city, s.state, s.zip,
         s.country, s.email_address, s.phone_num]
          .filter(Boolean).join(' ').toLowerCase().includes(qlc))
    : shipTos;
  const hasMain = shipTos.some((s) => !s.ship_to_num);

  // Badge counts every document regardless of the active filter, so switching to
  // Credit notes doesn't shrink it.
  const invoiceCount = invoiceStats
    ? (Number(invoiceStats.invoices) || 0) + (Number(invoiceStats.credit_notes) || 0)
    : 0;
  const openInvoices = invoiceStats ? Number(invoiceStats.open_invoices) || 0 : 0;
  const openArHint = invoiceStats
    ? (Number(invoiceStats.open_balance_amt) || 0) < 0
      ? `In credit · ${fmt(openInvoices)} open`
      : `${fmt(openInvoices)} open · Lifetime ${fmtAmt(invoiceStats.lifetime_invoiced_amt)}`
    : '';

  // credit === null: still loading. credit === undefined: no snapshot yet.
  // available_credit === null: the customer has no limit set, or their activity
  // spans currencies. All three render as an em dash, because none of them
  // means "unlimited" and a number here would imply the customer can buy.
  const creditValue = credit && credit.available_credit != null
    ? fmtAmt(credit.available_credit)
    : '—';
  const creditHint = (() => {
    if (credit === null) return 'Loading…';
    if (credit === undefined) return 'No snapshot yet — refresh to compute';
    if (credit.quarantine_reason) return 'Not assessable — see breakdown below';
    if (credit.available_credit == null) return 'No credit limit set in Epicor';
    if (credit.credit_hold) return 'On credit hold — cannot buy on account';
    if (credit.account_status !== 'active') return `Account ${credit.account_status}`;
    const over = Number(credit.over_limit_amt) || 0;
    if (over > 0) return `Over limit by ${fmtAmt(credit.over_limit_amt)}`;
    return `Limit ${fmtAmt(credit.credit_limit)} · Using ${fmtAmt(credit.credit_total)}`;
  })();

  // Whether the figure came from Epicor just now matters as much as the figure:
  // direct sales move exposure between reads, so a fallback served during an
  // Epicor outage can be wrong in the customer's favour.
  const creditSourceNote = (() => {
    if (!credit) return null;
    if (credit.read_mode === 'live') return { text: 'Live from Epicor', tone: 'ok' };
    const age = Number(credit.stale_seconds) || 0;
    const mins = Math.round(age / 60);
    const ago = mins < 1 ? 'under a minute' : mins < 60
      ? `${mins} min`
      : `${Math.round(mins / 60)} hr`;
    return {
      text: `Epicor unreachable — snapshot from ${ago} ago`,
      tone: credit.can_buy_on_account === false ? 'bad' : 'warn',
    };
  })();

  // Orders placed on the storefront that Epicor has not seen yet. Shown
  // separately so the tile's figure can be reconciled against Epicor's own
  // Customer Credit screen, which will not include these.
  const reservedAmt = Number(credit?.reserved_credit) || 0;

  return (
    <div className="oc-scope oc-detail-page">
      {/* ── Header ── */}
      <div className="oc-header oc-detail-header">
        <div className="oc-header-text">
          {backLink}
          <h1 className="oc-title">
            {c.cust_id || `#${c.cust_num}`}
            {c.name ? ` - ${c.name}` : ''}
          </h1>
          <div className="oc-metachips">
            <MetaChip label="Status" value={c.inactive ? 'Inactive' : 'Active'} tone={c.inactive ? 'bad' : 'ok'} />
            <MetaChip label="Group" value={c.group_code || '—'} tone="info" />
            <MetaChip label="Type" value={c.customer_type || '—'} tone="info" />
            <MetaChip label="Hold" value={c.credit_hold ? 'Hold' : 'OK'} tone={c.credit_hold ? 'bad' : 'ok'} />
            {!c.is_present && <MetaChip label="Sync" value="Absent" tone="bad" />}
          </div>
          <CustomerTagsPanel
            company={c.company}
            custNum={c.cust_num}
            tags={data.tags || []}
            onChange={(tags) => setData((prev) => ({ ...prev, tags }))}
          />
        </div>
        <div className="oc-fresh">
          <span>Key data current as of {fmtTime(c.synced_at)}</span>
          <span className="oc-readonly">Read-only</span>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="oc-stats">
        <Stat
          value={fmt(shipTos.length)}
          label="Ship-to addresses"
          hint={cities.size > 0
            ? `Across ${cities.size} ${cities.size === 1 ? 'city' : 'cities'}`
            : 'No city on record'}
        />
        <Stat label="BigCommerce link" {...linkStat} />
        <Stat
          value={fmt(bcAddrCount)}
          label="BC billing addresses"
          hint={bcAddrCount > 1
            ? `Billing + ${bcAddrCount - 1} more`
            : bcAddrCount === 1
              ? 'Billing address'
              : 'No addresses staged'}
        />
        <Stat value={c.company} label="Company" hint={c.currency_code || '—'} />
        <Stat
          value={orderStats ? fmt(Number(orderStats.open_orders) || 0) : '—'}
          label="Open orders"
          hint={orderStats
            ? `Lifetime ${fmtAmt(orderStats.lifetime_ordered_amt)}`
            : 'Open the Orders tab to load'}
          action={(
            <button
              type="button"
              className="oc-btn oc-btn-secondary oc-stat-action"
              onClick={() => setTab('orders')}
            >
              View orders
            </button>
          )}
        />
        {/* Posted open AR, not a net balance: closed credit notes carry a zero
            balance and so never reach this figure. Named "Open AR" to stay clear
            of the orders tab's pro-rata "Outstanding". */}
        <Stat
          value={invoiceStats ? fmtAmt(invoiceStats.open_balance_amt) : '—'}
          label="Open AR"
          hint={invoiceStats ? openArHint : 'Open the Invoices tab to load'}
          action={(
            <button
              type="button"
              className="oc-btn oc-btn-secondary oc-stat-action"
              onClick={() => setTab('invoices')}
            >
              View invoices
            </button>
          )}
        />
        {/* Mirrors Epicor's Customer Credit screen: limit minus open invoices
            and (where the CredInc flags allow) open orders. A null
            available_credit means not assessable and is shown as an em dash,
            never as an unlimited or zero figure. */}
        <Stat
          value={creditValue}
          label="Available credit"
          strong={credit?.can_buy_on_account === false}
          hint={creditHint}
          action={(
            <button
              type="button"
              className="oc-btn oc-btn-secondary oc-stat-action"
              onClick={syncCredit}
              disabled={creditSyncing}
            >
              {creditSyncing ? 'Refreshing…' : 'Refresh credit'}
            </button>
          )}
        />
      </div>
      {credit && (
        <CreditBreakdown
          credit={credit}
          provenance={creditSourceNote}
          reservedAmt={reservedAmt}
        />
      )}
      {creditError && <p className="oc-error">{creditError}</p>}

      {/* ── Tabbed frame ── */}
      <div className="oc-frame oc-blueprint">
        <Corners />
        <div className="oc-tabs" role="tablist" aria-label="Customer detail sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`oc-tab${tab === t.id ? ' on' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.id === 'shiptos' && <span className="oc-tab-count">{fmt(shipTos.length)}</span>}
              {t.id === 'orders' && orderTotal > 0 && (
                <span className="oc-tab-count">{fmt(orderTotal)}</span>
              )}
              {t.id === 'invoices' && invoiceCount > 0 && (
                <span className="oc-tab-count">{fmt(invoiceCount)}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Sales orders ── */}
        {tab === 'orders' && (
          <>
            {orderError && <div className="oc-error" role="alert">{orderError}</div>}
            {orderStats && (
              <p className="oc-hint" style={{ marginBottom: 12 }}>
                Open {fmt(Number(orderStats.open_orders) || 0)} ·
                Lifetime ordered {fmtAmt(orderStats.lifetime_ordered_amt)} ·
                Outstanding {fmtAmt(orderStats.open_outstanding_amt)}
              </p>
            )}
            <div className="oc-table-wrap">
              <table className="oc-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Ordered</th>
                    <th>Paid</th>
                    <th>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {orderRows === null && (
                    <tr><td colSpan={6}>Loading…</td></tr>
                  )}
                  {orderRows && orderRows.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <div className="oc-empty">
                          <p className="oc-empty-title">No sales orders staged</p>
                          <p className="oc-empty-sub">
                            Run a sales-orders sync from the Orders page to pull history.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {(orderRows || []).map((o) => (
                    <tr key={o.order_num}>
                      <td>
                        <Link to={`/orders/${o.company}/${o.order_num}`}>
                          {o.order_num}
                        </Link>
                        {o.po_num ? <div className="oc-hint">PO {o.po_num}</div> : null}
                      </td>
                      <td>{fmtOrderDate(o.order_date_effective || o.order_date)}</td>
                      <td>
                        {o.void_order ? 'Void' : o.order_held ? 'Held' : o.open_order ? 'Open' : 'Closed'}
                      </td>
                      <td>{fmtOrderAmt(orderedDisplay(o))}</td>
                      <td>{fmtOrderAmt(o.paid_amt)}</td>
                      <td>{fmtOrderAmt(o.outstanding_amt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {orderTotal > ORDER_PAGE && (
              <div className="oc-pager">
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  disabled={orderPage <= 0}
                  onClick={() => setOrderPage((p) => p - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {orderPage + 1} / {Math.ceil(orderTotal / ORDER_PAGE)}
                </span>
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  disabled={(orderPage + 1) * ORDER_PAGE >= orderTotal}
                  onClick={() => setOrderPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Invoices and credit notes ── */}
        {tab === 'invoices' && (
          <>
            {invoiceError && <div className="oc-error" role="alert">{invoiceError}</div>}
            <div className="oc-field" style={{ marginBottom: 12 }}>
              <span className="oc-field-label" id="oc-doctype-label">Show</span>
              <div className="oc-seg" role="radiogroup" aria-labelledby="oc-doctype-label">
                {DOC_TYPES.map((d) => (
                  <label key={d.id} className="oc-seg-opt">
                    <input
                      type="radio"
                      name="oc-doctype"
                      checked={docType === d.id}
                      onChange={() => changeDocType(d.id)}
                    />
                    <span>{d.label}</span>
                  </label>
                ))}
              </div>
            </div>
            {invoiceStats && (
              <p className="oc-hint" style={{ marginBottom: 12 }}>
                {fmt(Number(invoiceStats.invoices) || 0)} invoices ·
                {' '}{fmt(Number(invoiceStats.credit_notes) || 0)} credit notes ·
                {' '}Lifetime invoiced {fmtAmt(invoiceStats.lifetime_invoiced_amt)} ·
                {' '}Open AR {fmtAmt(invoiceStats.open_balance_amt)}
              </p>
            )}
            <div className="oc-table-wrap">
              <table className="oc-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceRows === null && (
                    <tr><td colSpan={6}>Loading…</td></tr>
                  )}
                  {invoiceRows && invoiceRows.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <div className="oc-empty">
                          <p className="oc-empty-title">
                            {docType === 'credit_memo'
                              ? 'No credit notes staged'
                              : 'No invoices staged'}
                          </p>
                          <p className="oc-empty-sub">
                            Run an invoices sync from the Orders page to pull history.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {(invoiceRows || []).map((iv) => (
                    <tr key={iv.invoice_num}>
                      <td>
                        <Link to={`/invoices/${iv.company}/${iv.invoice_num}`}>
                          {iv.invoice_num}
                        </Link>
                        {iv.credit_memo ? <div className="oc-hint">Credit note</div> : null}
                        {iv.po_num ? <div className="oc-hint">PO {iv.po_num}</div> : null}
                      </td>
                      <td>{fmtOrderDate(iv.invoice_date)}</td>
                      <td>{fmtOrderDate(iv.due_date)}</td>
                      <td>
                        {!iv.posted ? 'Unposted' : iv.open_invoice ? 'Open' : 'Closed'}
                      </td>
                      {/* Credit memos are stored negative; render as-is. */}
                      <td>{fmtOrderAmt(iv.doc_invoice_amt)}</td>
                      <td>{fmtOrderAmt(iv.doc_invoice_bal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {invoiceTotal > INVOICE_PAGE && (
              <div className="oc-pager">
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  disabled={invoicePage <= 0}
                  onClick={() => setInvoicePage((p) => p - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {invoicePage + 1} / {Math.ceil(invoiceTotal / INVOICE_PAGE)}
                </span>
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  disabled={(invoicePage + 1) * INVOICE_PAGE >= invoiceTotal}
                  onClick={() => setInvoicePage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Ship-to addresses ── */}
        {tab === 'shiptos' && (
          <>
            <div className="oc-shiptools">
              <div className="oc-shipsearch">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.8-3.8" />
                </svg>
                <input
                  type="text"
                  className="oc-input"
                  placeholder="Search ship-tos…"
                  aria-label="Search ship-to addresses"
                  value={shipQuery}
                  onChange={(e) => setShipQuery(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="oc-btn oc-btn-secondary"
                disabled={shipSyncing}
                onClick={syncShipTos}
              >
                {shipSyncing ? 'Syncing…' : 'Sync ship-tos from Epicor'}
              </button>
            </div>
            {shipError && <div className="oc-error" role="alert">{shipError}</div>}
            {shipLive && (
              <p className="oc-shiplive">
                Job #{shipLive.id} — {shipLive.status}: fetched {fmt(Number((shipLive.counters || {}).fetched) || 0)},
                {' '}upserted {fmt(Number((shipLive.counters || {}).upserted) || 0)},
                {' '}marked absent {fmt(Number((shipLive.counters || {}).marked_absent) || 0)}.
                {shipLive.error_detail ? ` Error: ${shipLive.error_detail}` : ''}
              </p>
            )}
            <div className="oc-table-wrap">
              <table className="oc-table">
                <caption className="sr-only">Ship-to addresses for {c.name || c.cust_id}</caption>
                <colgroup>
                  <col className="oc-col-shipnum" />
                  <col />
                  <col className="oc-col-city" />
                  <col className="oc-col-phone" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Ship-to #</th>
                    <th scope="col">Contact &amp; address</th>
                    <th scope="col">City</th>
                    <th scope="col">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {shipTos.length === 0 && (
                    <tr>
                      <td colSpan={4}>
                        <div className="oc-empty">
                          <p className="oc-empty-title">No ship-to addresses staged</p>
                          <p className="oc-empty-sub">Run “Sync ship-tos from Epicor” to pull them.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {shipTos.length > 0 && shownShipTos.length === 0 && (
                    <tr>
                      <td colSpan={4}>
                        <div className="oc-empty">
                          <p className="oc-empty-title">Nothing matches that</p>
                          <p className="oc-empty-sub">Try a shorter search.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {shownShipTos.map((s) => {
                    const addr = addrLine(s);
                    const region = regionLine(s);
                    return (
                      <tr key={s.ship_to_num || '(main)'}>
                        <td className="oc-shipnum">{s.ship_to_num || '0001'}</td>
                        <td className="oc-contact">
                          <div className="oc-contact-name">{s.name || '—'}</div>
                          {(addr || region) && (
                            <div className="oc-contact-addr">{[addr, region].filter(Boolean).join(', ')}</div>
                          )}
                          {s.email_address && <div className="oc-contact-email">{s.email_address}</div>}
                          {s.inactive && <div className="oc-tagline">Not used for shipping</div>}
                          {!s.is_present && <div className="oc-tagline">Absent from last sync</div>}
                        </td>
                        <td>{s.city || '—'}</td>
                        <td className="oc-phone">{s.phone_num || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {shipTos.length > 0 && (
              <div className="oc-table-foot">
                {qlc
                  ? `Showing ${fmt(shownShipTos.length)} of ${fmt(shipTos.length)}`
                  : `Showing all ${fmt(shipTos.length)}`}
                {' '}{shipTos.length === 1 ? 'address' : 'addresses'}
                {hasMain ? ' · 0001 is the billing address' : ''}
              </div>
            )}
          </>
        )}

        {/* ── BigCommerce link ── */}
        {tab === 'link' && (
          <div className="oc-linktab">
            <div className="oc-linktools">
              <p className="oc-note">
                Storefront snapshots are read-only — nothing is pushed to BigCommerce.
              </p>
              <button
                type="button"
                className="oc-btn oc-btn-secondary"
                disabled={refreshing}
                onClick={refreshBC}
              >
                {refreshing ? 'Refreshing…' : 'Refresh store snapshot'}
              </button>
            </div>

            {linked ? (
              <div className="oc-linkcard oc-blueprint">
                <Corners />
                <div className="oc-linkcard-head">
                  <span className="oc-linkchip linked">Linked</span>
                  <span className="oc-muted">
                    BC #{mapping.bc_customer_id} · {mapping.store_code} · {mapping.match_method}
                    {' '}· matched {fmtTime(mapping.matched_at)}
                  </span>
                </div>
                {bc ? (
                  <>
                    <dl className="oc-kv">
                      <div><dt>Name</dt><dd>{[bc.first_name, bc.last_name].filter(Boolean).join(' ') || '—'}</dd></div>
                      <div><dt>Company</dt><dd>{bc.company || '—'}</dd></div>
                      <div><dt>Email</dt><dd>{bc.email || '—'}</dd></div>
                      <div><dt>Phone</dt><dd>{bc.phone || '—'}</dd></div>
                      <div><dt>Customer group</dt><dd>{bc.customer_group_id ?? '—'}</dd></div>
                      <div><dt>Addresses</dt><dd>{(bc.addresses || []).length}</dd></div>
                    </dl>
                    {bcAttrs.length > 0 && (
                      <>
                        <h4 className="oc-subhead">BC attributes</h4>
                        <dl className="oc-kv">
                          {bcAttrs.map((a) => (
                            <div key={a.id}><dt>attr #{a.attribute_id}</dt><dd>{a.attribute_value}</dd></div>
                          ))}
                        </dl>
                      </>
                    )}
                  </>
                ) : (
                  <p className="oc-note">
                    A mapping exists but no storefront snapshot is staged yet — refresh the store
                    snapshot to see the linked record.
                  </p>
                )}
                <div className="oc-linkcard-actions">
                  <button
                    type="button"
                    className="oc-btn oc-btn-secondary"
                    disabled={saving}
                    onClick={() => link(null)}
                  >
                    Unlink (clear mapping)
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="oc-note oc-linknote">
                  Not linked to a BigCommerce customer. This row is{' '}
                  {c.match_status === 'ambiguous'
                    ? 'flagged ambiguous — pick the right storefront record below.'
                    : 'importable in a future push phase.'}
                </p>
                {candidates.length === 0 ? (
                  <div className="oc-empty">
                    <p className="oc-empty-title">No candidates</p>
                    <p className="oc-empty-sub">
                      Refresh the store snapshot, or none share CustNum, CustID or email.
                    </p>
                  </div>
                ) : (
                  <div className="oc-cands">
                    {candidates.map((cand) => (
                      <div key={cand.bc_customer_id} className="oc-cand oc-blueprint">
                        <Corners />
                        <div className="oc-cand-info">
                          <span className="oc-cand-id">BC #{cand.bc_customer_id}</span>
                          <span className="oc-cand-meta">
                            {[
                              cand.email,
                              cand.epicor_cust_id ? `CustID ${cand.epicor_cust_id}` : null,
                              cand.epicor_cust_num != null ? `CustNum ${cand.epicor_cust_num}` : null,
                              cand.company_name,
                            ].filter(Boolean).join(' · ')}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="oc-btn oc-btn-primary"
                          disabled={saving}
                          onClick={() => link(cand.bc_customer_id)}
                        >
                          Link this one
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Activity ── */}
        {tab === 'activity' && (
          <div className="oc-activity">
            {jobs === null && <p className="oc-note oc-activity-loading">Loading…</p>}
            {jobs !== null && jobs.length === 0 && (
              <div className="oc-empty">
                <p className="oc-empty-title">No targeted runs for this customer yet</p>
                <p className="oc-empty-sub">Use “Sync ship-tos from Epicor” to run one.</p>
              </div>
            )}
            {jobs !== null && jobs.map((j) => (
              <div key={j.id} className="oc-jobrow">
                <Link to={`/jobs/history/${j.id}`} className="oc-jobid">#{j.id}</Link>
                <span className="oc-jobstage">{(j.stage_filter || []).join(', ') || '—'}</span>
                <StatusBadge status={j.status} />
                <span className="oc-muted oc-jobtime">
                  {j.finished_at ? `Finished ${fmtTime(j.finished_at)}` : `Created ${fmtTime(j.created_at)}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetailPage;
